import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import { toast } from "sonner";
import { ArrowClockwise, PlugsConnected } from "@phosphor-icons/react";

export default function IntegrationHealth() {
  const [health, setHealth] = useState(null);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const [h, e] = await Promise.all([
        api.get("/integration/health"),
        api.get(`/integration/events${filter?`?status=${filter}`:""}`),
      ]);
      setHealth(h.data); setEvents(e.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const retry = async (id) => {
    try { await api.post(`/integration/events/${id}/retry`); toast.success("Retried & delivered"); load(); }
    catch { toast.error("Retry failed"); }
  };

  const injectFail = async () => {
    try { await api.post("/integration/simulate/failure", { direction: "outbound", event_type: "order.updated", payload: { demo: true }, error: "Website returned 503" }); toast.success("Failed event injected"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="integration-page" className="page-fade">
      <PageHeader eyebrow="Reliability" title="Integration health" description="Live view of Website ↔ ERP synchronisation. Retry failed webhooks manually or inject test failures." actions={
        <button onClick={injectFail} className="btn-ghost" data-testid="inject-failure-btn">Inject test failure</button>
      }/>
      {health && (
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="card-elevated" data-testid="ih-status"><div className="label-eyebrow">Status</div><div className="mt-4"><StatusChip value={health.connected ? "ok" : "failed"}/></div></div>
          <div className="card-elevated"><div className="label-eyebrow">Last order</div><div className="text-sm mt-4">{fmtDate(health.last_order_received)}</div></div>
          <div className="card-elevated"><div className="label-eyebrow">Failed webhooks</div><div className="metric-num mt-3">{health.failed_webhooks || 0}</div></div>
          <div className="card-elevated"><div className="label-eyebrow">Pending retries</div><div className="metric-num mt-3">{health.pending_retries || 0}</div></div>
          <div className="card-elevated"><div className="label-eyebrow">Duplicates blocked</div><div className="metric-num mt-3">{health.duplicate_attempts || 0}</div></div>
          <div className="card-elevated"><div className="label-eyebrow">Avg latency</div><div className="metric-num mt-3">{health.avg_latency_ms || 0}<span className="text-sm text-muted-foreground ml-1">ms</span></div></div>
          <div className="card-elevated"><div className="label-eyebrow">Last product sync</div><div className="text-sm mt-4">{fmtDate(health.last_product_update)}</div></div>
        </div>
      )}
      <div className="flex gap-2 mb-4 flex-wrap">
        {["","pending","delivered","failed","retrying","duplicate"].map((s) => (
          <button key={s||"all"} onClick={()=>setFilter(s)} data-testid={`ih-filter-${s||"all"}`}
                  className={`pill-toggle ${filter===s?"is-active":""}`}>{s || "All"}</button>
        ))}
      </div>
      <div className="card-soft p-0 overflow-x-auto">
        <table className="table-modern">
          <thead><tr><th>When</th><th>Direction</th><th>Event</th><th>Status</th><th>Attempts</th><th>Error</th><th></th></tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e._id} data-testid={`event-row-${e._id}`}>
                <td className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(e.created_at)}</td>
                <td className="text-sm capitalize">{e.direction}</td>
                <td className="font-mono text-xs">{e.event_type}</td>
                <td><StatusChip value={e.status}/></td>
                <td className="tabular">{e.attempts}</td>
                <td className="text-xs text-destructive max-w-xs truncate">{e.last_error || "—"}</td>
                <td className="text-right">
                  {(e.status === "failed" || e.status === "retrying") && (
                    <button onClick={()=>retry(e._id)} className="btn-ghost btn-sm" data-testid={`retry-${e._id}`}><ArrowClockwise size={12}/> Retry</button>
                  )}
                </td>
              </tr>
            ))}
            {!events.length && <tr><td colSpan={7}><EmptyState icon={PlugsConnected} title="No events yet" description="Website ↔ ERP webhooks will appear here in real time." /></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
